import { useEffect, useState } from 'react';
import { Checkbox, DatePicker, Menu, Typography, Button, Row, Col } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { DatePickerProps, RangePickerProps } from 'antd/lib/date-picker';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import { selectTask, selectUsersCreatedBy, setTaskFilters } from '../../../store/slice/taskSlice';
import { getUserProfile, selectListTaskType } from '../../../store/slice/userSlice';
import SortFilters from '../SortFilters/SortFilters';
import Task from '../../../models/Task';
import './DropDownFilters.less';
import SelectUsers from '../../Molecules/SelectUsers/SelectUsers';
import moment from 'moment';

/**
 * @component DropDownFilters.tsx
 * @description Componente DropDownFilters encargado de contener los distintos tipos de filtros
 * (Search/estados de la tarea/Asignadas a/sort(desc/asc)/filtado por fechas/creadas por/tipo de tarea).
 * El componente usa redux para guardar los filtros y asi poder filtrar en conjunto con un Type(tipo de filtro) y value(valor del filtro), 
 * el componente usa un state local para la ui y limpiar los inputs.
 * @param {boolean} closeMenu : Close the dropdown menu
 * @return { Component }
 */
interface DropDownFiltersProps {
  closeMenu: (isVisible: boolean) => void;
}

function DropDownFilters(props: DropDownFiltersProps) {
  const { Text } = Typography;
  const dispatch = useAppDispatch();
  const listTaskType = useAppSelector(selectListTaskType);
  const tasksState = useAppSelector(selectTask);
  const usersCreatedBy = useAppSelector(selectUsersCreatedBy);
  const { RangePicker } = DatePicker;
  const [isDateRange, setIsDateRange] = useState(false);
  const [userTasks, setUserTasks] = useState<Task[]>([]);
  const [enableDateInput, setEnableDateInput] = useState(false);
  const filters = useAppSelector(state => state.task.filtros);

  const taskTypeOnChange = (checkedValues: CheckboxValueType[]) => {
    dispatch(setTaskFilters({tipoTarea: checkedValues}))
  };

  const userSelectOnChange = (selectValue: Array<string>) => {
    dispatch(setTaskFilters({creadasPor: [...selectValue]}))
  };

  /**
   * Encargado de estar a la escucha en los cambios de filtro tipo
   * sort (-createdAt/+createdAt/-titulo/+titulo) en el radio group
   * @param sortValue
   */
  const sortsOnChange = (sortValue: string) => {
    dispatch(setTaskFilters({ordenarPor: sortValue}))
  };

  /**
   * Encargado de estar a la escucha en los cambios de filtro tipo fecha
   * (createdAt/fechaRealizacion(closedAt)) en el radio group
   * @param filterType
   */
  const dateSortsOnChange = (filterType: 'createdAt' | 'fechaRealizacion' | 'closedAt') => {
    setEnableDateInput(true);
    dispatch(setTaskFilters({ tipoFecha:  filterType}))
  };

  /**
   * Encargado de estar a la escucha en los cambios del input Fecha(DatePicker)
   */
  const datePickerOnChange: DatePickerProps['onChange'] = (
    date,
  ) => {
    dispatch(setTaskFilters({ fechaDesde: date ?? undefined }))
  };

  // Muestra el input de rango fecha(RangePicker) si es seleccionado
  const dateRangeCheckBoxOnChange = (showDateRange: CheckboxChangeEvent) => {
    setIsDateRange(showDateRange.target.checked);
  };

  /**
   * Encargado de estar a la escucha en los cambios del input Rango Fecha(RangePicker)
   */
  const rangePickerOnChange: RangePickerProps['onChange'] = (
    dates,
  ) => {
    dispatch(setTaskFilters({
      fechaDesde: dates && dates[0] ? dates[0] : undefined,
      fechaHasta: dates && dates[1] ? dates[1] : undefined,
    }))
  };

  const closeMenuOnClick = () => {
    props.closeMenu(false);
  };

  const cleanFiltersOnClick = () => {
    /**
     * Limpiando filtros y inputs
     */
    dispatch(setTaskFilters({
      ordenarPor: undefined,
      tipoFecha: undefined,
      fechaDesde: undefined,
      fechaHasta: undefined,
      creadasPor: [],
      tipoTarea: [],
    }))
    setIsDateRange(false);
    setEnableDateInput(false);
  };

  useEffect(() => {
    if (userTasks.length === 0) {
      setUserTasks(tasksState.task);
    }
  }, [userTasks, tasksState.task]);
  
  useEffect(() => {
    dispatch(getUserProfile());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Desabilita las fechas mayores a las actuales
  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > moment().endOf('day');
  };

  return (
    <Menu
      style={{maxWidth: '645px'}}
      className="at-drop-down__menu"
      items={[
        {
          key: '1',
          label: (
            <div className="ml-drop-filter__order">
              <Text className="label">Ordenar por</Text>
              <SortFilters
                sortsOnChange={sortsOnChange}
                isDateSort={false}
                value={filters.ordenarPor as string}
              />
            </div>
          ),
        },
        {
          type: 'divider',
          className: 'at-drop-down__menu__divider',
        },
        {
          key: '2',
          label: (
            <div className="ml-drop-filter__create__by">
              <Text className="label">Fecha</Text>
              <div className="container">
                <SortFilters
                  sortsOnChange={dateSortsOnChange}
                  isDateSort={true}
                  value={filters.tipoFecha as string}
                />
                <div className="date-picker__container">
                  {isDateRange ? (
                    <RangePicker
                      disabled={!enableDateInput ? true : false}
                      size="large"
                      placeholder={["Fecha inicial","Fecha limite"]}
                      className="range__date-picker"
                      format={'DD/MM/YYYY'}
                      allowClear={true}
                      disabledDate={filters.tipoFecha === 'createdAt' ? disabledDate : undefined}
                      onChange={rangePickerOnChange}
                      value={[filters.fechaDesde ?? null, filters.fechaHasta ?? null]}
                    />
                  ) : (
                    <DatePicker
                      disabled={!enableDateInput ? true : false}
                      className="date-picker"
                      size="large"
                      disabledDate={filters.tipoFecha === 'createdAt' ? disabledDate : undefined}
                      placeholder="Elige una fecha"
                      allowClear={true}
                      onChange={datePickerOnChange}
                      format={'DD/MM/YYYY'}
                      value={filters.fechaDesde}
                    />
                  )}
                  <Checkbox
                    className="date-range__checkbox"
                    onChange={dateRangeCheckBoxOnChange}
                    checked={isDateRange}
                  >
                    Rango de fechas
                  </Checkbox>
                </div>
              </div>
            </div>
          ),
        },
        {
          type: 'divider',
          className: 'at-drop-down__menu__divider',
        },
        {
          key: '3',
          label: (
            <div className="ml-drop-filter__users__select">
              <Text className="label">Creadas por</Text>
              <SelectUsers value={filters.creadasPor as string[]} onChange={userSelectOnChange} placeholder={'Buscar'} users={usersCreatedBy} avatarCount={4} className={'input'}/>
            </div>
          ),
        },
        {
          type: 'divider',
          className: 'at-drop-down__menu__divider',
        },
        {
          key: '4',
          label: (
            <div className="ml-drop-filter__order">
              <Text className="label">Tipo de Tarea</Text>{' '}
              <Checkbox.Group
                onChange={taskTypeOnChange}
                value={filters.tipoTarea}
              >
                <Row>
                {listTaskType!.map((item) => (
                  <Col span={12}>
                    <Checkbox value={item.id} key={item.id}>
                    {item.tipotarea}
                    </Checkbox>
                  </Col>
                ))}
                </Row>
              </Checkbox.Group>
            </div>
          ),
        },
        {
          key: '5',
          label: (
            <div className="ml-drop-filter__buttons">
              <Button
                onClick={cleanFiltersOnClick}
                shape="round"
                className="clear__button"
              >
                Limpiar Filtros
              </Button>
              <Button type={'primary'} shape="round" onClick={closeMenuOnClick}>
                Aplicar
              </Button>
            </div>
          ),
        },
      ]}
    />
  );
}

export default DropDownFilters;